var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import ArchiveIcon from '@mui/icons-material/Archive';
import { Check as CheckIcon, Error as ErrorIcon } from '@mui/icons-material';
import { Alert, Box, TextField } from '@mui/material';
import { useOrderListItem, toOptions, usePaymentTimingForPriceVersion, useInvoiceListItem, usePreOrderRequestItem, } from './useListItem';
import { validURL, weightFormat } from '../utils/format';
import { useRoasterCountries } from './useRoasterCountries';
import { useOffice } from './useOffice';
import OrderStatusTag from '../components/atoms/OrderStatusTag';
import DeliveryRequestStatusTag from '../components/atoms/DeliveryRequestStatusTag';
import CheckboxGroup from '../components/atoms/CheckboxGroup';
import { packages } from '../constants/offerValues';
import YearPicker from '../components/MaterialTable/YearPicker';
import EditDateTimePickerWithTimeZone from '../components/MaterialTable/EditDateTimePickerWithTimeZone';
import MonthPicker from '../components/MaterialTable/MonthPicker';
import HourPicker from '../components/MaterialTable/HourPicker';
import { TradeCounties } from '../constants/roasterValues';
import ForumButton from '../components/molecules/ForumButton';
import CurrencyTextField from '../components/organisms/CurrencyTextField';
import AdminNewOrderButton from '../components/organisms/AdminNewOrderButton';
import DownloadOfferListCsvButton from '../components/organisms/DownloadOfferListCsvButton';
import StockFilter from '../components/molecules/StockFilter';
import DeliveryRequestItemList from '../components/organisms/DeliveryRequestItemList';
import { formatMoneyAmount, formatPercentage, formatDateTimeWithOffset, formatWaterActivity, } from '../utils/format';
import { EstimatedDeliveryPeriodMonthParts, StatusValues, } from '../constants/transportToWarehouseValues';
import NumericTextField from '../components/MaterialTable/NumericTextField';
import BidStatusTag from '../components/atoms/BidStatusTag';
import { AuctionIcon, DeliveryRequestIcon } from '../components/atoms/Icons';
import { currencyOptions } from '../constants/currency';
import OfferListLinks from '../components/molecules/OfferListLinks';
import { camelize, decamelize } from '../utils/camelize';
import TransportToWarehouseStatus from '../components/atoms/TransportToWarehouseStatus';
import qs from 'qs';
import StatusTag from '../components/atoms/StatusTag';
import { MenuItem, Select } from '@mui/material';
import OfficeFilter from '../components/molecules/OfficeFilter';
import RoasterFilter from '../components/molecules/RoasterFilter';
import DateRangeFilter from '../components/molecules/DateRangeFilter';
import EditDateTimePicker from '../components/MaterialTable/EditDateTimePicker';
export var column = function (field, values) { return (__assign({ field: field }, (values || {}))); };
export var currencyColumn = function (field, values) {
    return column(field, __assign({ lookup: currencyOptions }, (values || {})));
};
export var numericColumn = function (field, values) {
    return column(field, __assign({ type: 'numeric', align: 'right' }, (values || {})));
};
export var hourColumn = function (field, values) {
    return numericColumn(field, __assign({ render: function (record) { return "".concat(record[field], ":00"); }, editComponent: HourPicker }, (values || {})));
};
export var dateColumn = function (field, values) {
    return column(field, __assign({ type: 'date' }, (values || {})));
};
export var datetimeColumn = function (field, values) {
    return column(field, __assign({ type: 'datetime' }, (values || {})));
};
export var booleanColumn = function (field, values) {
    return column(field, __assign({ type: 'boolean' }, (values || {})));
};
export var moneyColumn = function (field, values) {
    var getCurrency = values.getCurrency, otherValues = __rest(values, ["getCurrency"]);
    var EditComponent = function (props) {
        return (React.createElement(CurrencyTextField, { value: props.value || '', onChange: props.onChange, currency: getCurrency(props.rowData) }));
    };
    return column(field, __assign({ render: function (record) {
            return formatMoneyAmount(record[field], getCurrency(record));
        }, editComponent: EditComponent, align: 'right' }, otherValues));
};
export function actionColumn(render) {
    return {
        title: ' ',
        sorting: false,
        render: render,
        cellStyle: {
            padding: 0,
        },
        headerStyle: {
            padding: 0,
        },
    };
}
export function useUserColumns(_a) {
    var orderBy = _a.orderBy, orderDirection = _a.orderDirection;
    var defaultSort = function (field) {
        if (orderBy === field &&
            (orderDirection === 'asc' || orderDirection === 'desc')) {
            return orderDirection;
        }
        return;
    };
    return {
        name: column('name', {
            defaultSort: defaultSort('name'),
            validate: function (rowData) { return rowData.name !== ''; },
        }),
        email: column('email', { defaultSort: defaultSort('email') }),
        isCommunityManager: column('isCommunityManager', {
            render: function (user) { return (user.isCommunityManager ? React.createElement(CheckIcon, null) : ''); },
            defaultSort: defaultSort('isCommunityManager'),
        }),
    };
}
export function useRoasterColumns(_a) {
    var filters = _a.filters, orderBy = _a.orderBy, orderDirection = _a.orderDirection;
    var t = useTranslation().t;
    var roasterCountryItems = useRoasterCountries().roasterCountryItems;
    var officeItems = useOffice().officeItems;
    var renderRoasterCountry = React.useCallback(function (roaster) {
        var code = roaster.country.code;
        return roasterCountryItems[code];
    }, [roasterCountryItems]);
    var renderCountryOffice = React.useCallback(function (roaster) {
        var name = roaster.country.office.name;
        return name;
    }, []);
    var defaultSort = function (field) {
        if (orderBy === field &&
            (orderDirection === 'asc' || orderDirection === 'desc')) {
            return orderDirection;
        }
        return;
    };
    return {
        id: column('id', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.id,
            filterCellStyle: {
                minWidth: 90,
            },
        }),
        communityManager: column('communityManager', {
            render: function (_a) {
                var communityManager = _a.communityManager;
                return communityManager === null || communityManager === void 0 ? void 0 : communityManager.name;
            },
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.communityManager,
            defaultSort: defaultSort('communityManager'),
        }),
        customerType: column('customerType', {
            lookup: t('params.roaster.customerTypes', {
                returnObjects: true,
            }),
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.customerType,
            defaultSort: defaultSort('customerType'),
        }),
        company: column('company', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.company,
            defaultSort: defaultSort('company'),
        }),
        storeType: column('storeType', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.storeType,
            lookup: t('params.roaster.storeTypes', { returnObjects: true }),
            defaultSort: defaultSort('storeType'),
        }),
        storeName: column('storeName', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.storeName,
            defaultSort: defaultSort('storeName'),
        }),
        lastname: column('lastname', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.lastname,
            defaultSort: defaultSort('lastname'),
        }),
        firstname: column('firstname', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.firstname,
            defaultSort: defaultSort('firstname'),
        }),
        nameKana: column('nameKana', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.nameKana,
            defaultSort: defaultSort('nameKana'),
        }),
        email: column('email', {
            title: t('params.user.email'),
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.email,
            defaultSort: defaultSort('email'),
        }),
        phone: column('phone', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.phone,
            defaultSort: defaultSort('phone'),
        }),
        countryCallingCode: column('countryCallingCode', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.countryCallingCode,
            defaultSort: defaultSort('countryCallingCode'),
        }),
        country: {
            field: 'country.code',
            title: t('params.roasterCountry.code'),
            lookup: roasterCountryItems,
            render: renderRoasterCountry,
            defaultFilter: filters ? filters['country.code'] : undefined,
            sorting: false,
        },
        countryOffice: {
            field: 'country.office.name',
            title: t('params.office.name'),
            lookup: officeItems,
            render: renderCountryOffice,
            defaultFilter: filters ? filters['country.office.name'] : undefined,
            sorting: false,
        },
        postalCode: column('postalCode', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.postalCode,
            defaultSort: defaultSort('postalCode'),
        }),
        prefecture: column('prefecture', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.prefecture,
            defaultSort: defaultSort('prefecture'),
        }),
        city: column('city', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.city,
            defaultSort: defaultSort('city'),
        }),
        address: column('address', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.address,
            defaultSort: defaultSort('address'),
        }),
        building: column('building', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.building,
            defaultSort: defaultSort('building'),
        }),
        roasterMaker: column('roasterMaker', {
            lookup: t('params.roaster.roasterMakerOptions', {
                returnObjects: true,
            }),
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.roasterMaker,
            defaultSort: defaultSort('roasterMaker'),
        }),
        roasterMaxCapacity: column('roasterMaxCapacity', {
            filtering: false,
            defaultSort: defaultSort('roasterMaxCapacity'),
        }),
        roastedPerMonth: column('roastedPerMonth', {
            filtering: false,
            defaultSort: defaultSort('roastedPerMonth'),
        }),
        communityEngagement: column('communityEngagement', {
            filtering: true,
            lookup: t('params.roaster.communityEngagements', {
                returnObjects: true,
            }),
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.communityEngagement,
            defaultSort: defaultSort('communityEngagement'),
        }),
        typicaShareRatio: column('typicaShareRatio', {
            filtering: false,
            render: function (_a) {
                var typicaShareRatio = _a.typicaShareRatio;
                return formatPercentage(typicaShareRatio || '');
            },
            defaultSort: defaultSort('typicaShareRatio'),
        }),
        updatedAt: datetimeColumn('updatedAt', {
            filtering: false,
            defaultSort: defaultSort('updatedAt'),
        }),
        createdAt: datetimeColumn('createdAt', {
            filtering: false,
            defaultSort: defaultSort('createdAt'),
        }),
    };
}
export function useContributeToOkagesamaNoMoriColumns() {
    var t = useTranslation().t;
    return {
        roasterId: {
            title: t('params.roasterId'),
            render: function (contributeToOkagesamaNoMori) {
                return contributeToOkagesamaNoMori.roaster.id;
            },
        },
        roasterLastname: {
            title: t('params.roaster.lastname'),
            render: function (contributeToOkagesamaNoMori) {
                return contributeToOkagesamaNoMori.roaster.lastname;
            },
        },
        roasterFirstname: {
            title: t('params.roaster.firstname'),
            render: function (contributeToOkagesamaNoMori) {
                return contributeToOkagesamaNoMori.roaster.firstname;
            },
        },
        price: {
            title: t('params.contributeToOkagesamaNoMori.price'),
            render: function (contributeToOkagesamaNoMori) {
                return formatMoneyAmount(parseFloat(contributeToOkagesamaNoMori.price), contributeToOkagesamaNoMori.roaster.country.currency);
            },
            sorting: false,
        },
    };
}
export function useProducerColumns(_a) {
    var filters = _a.filters, orderBy = _a.orderBy, orderDirection = _a.orderDirection;
    var t = useTranslation().t;
    var countryMap = {};
    TradeCounties.forEach(function (code) { return (countryMap[code] = t("countries.".concat(code))); });
    var defaultSort = function (field) {
        if (orderBy === field &&
            (orderDirection === 'asc' || orderDirection === 'desc')) {
            return orderDirection;
        }
        return;
    };
    return {
        id: column('id', {
            filtering: false,
            filterCellStyle: {
                minWidth: 10,
            },
        }),
        company: column('company', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.company,
            defaultSort: defaultSort('company'),
        }),
        lastname: column('lastname', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.lastname,
            defaultSort: defaultSort('lastname'),
        }),
        firstname: column('firstname', {
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.firstname,
            defaultSort: defaultSort('firstname'),
        }),
        email: column('email', {
            title: t('params.user.email'),
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.email,
            defaultSort: defaultSort('email'),
        }),
        country: column('country', {
            title: t('params.producer.country'),
            lookup: countryMap,
            render: function (producer) { return t("countries.".concat(producer.country.toUpperCase())); },
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.country,
            defaultSort: defaultSort('country'),
        }),
        farmersCount: numericColumn('farmersCount', { filtering: false }),
        createdAt: datetimeColumn('createdAt', {
            filtering: false,
            defaultSort: defaultSort('createdAt'),
        }),
    };
}
export function useOfferListColumns(role) {
    var t = useTranslation().t;
    var sortCountry = React.useCallback(function (a, b) {
        var countryA = t("countries.".concat(a.producer.country.toUpperCase()));
        var countryB = t("countries.".concat(b.producer.country.toUpperCase()));
        return countryA.localeCompare(countryB);
    }, [t]);
    var sortProducer = React.useCallback(function (a, b) {
        var producerA = "".concat(a.producer.firstname, " ").concat(a.producer.lastname);
        var producerB = "".concat(b.producer.firstname, " ").concat(b.producer.lastname);
        return producerA.localeCompare(producerB);
    }, []);
    var renderAdminOrderAction = React.useCallback(function (offerList) {
        return React.createElement(AdminNewOrderButton, { offerList: offerList });
    }, []);
    var renderCsvDownloadAction = React.useCallback(function (offerList) {
        if (!offerList.csvDownloadable) {
            return null;
        }
        return React.createElement(DownloadOfferListCsvButton, { offerList: offerList });
    }, []);
    var renderForumAction = React.useCallback(function (offerList) {
        if (!role) {
            return null;
        }
        return React.createElement(ForumButton, { offerListId: offerList.id, iconOnly: true, role: role });
    }, [role]);
    var renderSampleOrderStartedAt = React.useCallback(function (offerList) {
        var officeDetails = offerList.officeDetails;
        if (!officeDetails)
            return;
        return officeDetails
            .map(function (officeDetail) {
            return "".concat(officeDetail.office.name, ": ").concat(formatDateTimeWithOffset(officeDetail.sampleOrderStartedAt));
        })
            .join('\n');
    }, []);
    var renderSampleOrderExpiredAt = React.useCallback(function (offerList) {
        var officeDetails = offerList.officeDetails;
        if (!officeDetails)
            return;
        return officeDetails
            .map(function (officeDetail) {
            return "".concat(officeDetail.office.name, ": ").concat(formatDateTimeWithOffset(officeDetail.sampleOrderExpiredAt));
        })
            .join('\n');
    }, []);
    var renderContributeToOkagesamaNoMoris = React.useCallback(function (offerList) {
        var contributableOkagesamaNoMori = offerList.contributableOkagesamaNoMori, hasOkagesamaNoMoriContributors = offerList.hasOkagesamaNoMoriContributors, okagesamaNoMoriPaid = offerList.okagesamaNoMoriPaid;
        if (!contributableOkagesamaNoMori && !hasOkagesamaNoMoriContributors)
            return '';
        return t("pages.offerList.okagesamaNoMoriStatuses.".concat(okagesamaNoMoriPaid ? 'paid' : 'joined'));
    }, [t]);
    var sortContributeToOkagesamaNoMoris = React.useCallback(function (offerListA, offerListB) {
        return renderContributeToOkagesamaNoMoris(offerListA).localeCompare(renderContributeToOkagesamaNoMoris(offerListB));
    }, [renderContributeToOkagesamaNoMoris]);
    var renderOfficeFilter = React.useCallback(function (props) { return React.createElement(OfficeFilter, __assign({}, props)); }, []);
    return {
        producerCountry: {
            title: t('params.producer.country'),
            customSort: sortCountry,
            render: function (offerList) {
                return t("countries.".concat(offerList.producer.country.toUpperCase()));
            },
        },
        producerName: {
            title: t('labels.curatorName'),
            customSort: sortProducer,
            render: function (offerList) {
                return "".concat(offerList.producer.firstname, " ").concat(offerList.producer.lastname);
            },
        },
        id: column('id'),
        name: column('name'),
        publishedDate: column('publishedDate'),
        expiredAt: column('expiredAt', {
            render: function (offerList) {
                return formatDateTimeWithOffset(offerList.expiredAt);
            },
        }),
        sampleDeliveryDate: column('sampleDeliveryDate'),
        status: column('status', {
            lookup: t('params.offerList.statuses', {
                returnObjects: true,
            }),
        }),
        createdAt: datetimeColumn('createdAt', { filtering: false }),
        comment: column('comment'),
        ordersCount: numericColumn('ordersCount'),
        orderItemsTotalQuantity: numericColumn('orderItemsTotalQuantity'),
        orderItemsTotalWeight: numericColumn('orderItemsTotalWeight'),
        typicaStockTotalQuantity: numericColumn('typicaStockTotalQuantity'),
        typicaStockTotalWeight: numericColumn('typicaStockTotalWeight'),
        sampleOrdersCount: numericColumn('sampleOrdersCount'),
        paymentTerm: column('paymentTerm', {
            lookup: t('params.offerList.paymentTerms', {
                returnObjects: true,
            }),
        }),
        orderStartedAt: column('orderStartedAt', {
            render: function (offerList) {
                return formatDateTimeWithOffset(offerList.orderStartedAt);
            },
        }),
        officeDetailsSampleOrderStartedAt: {
            title: t('params.offerListOfficeDetail.sampleOrderStartedAt'),
            render: renderSampleOrderStartedAt,
            cellStyle: {
                minWidth: 350,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
            },
        },
        officeDetailsSampleOrderExpiredAt: {
            title: t('params.offerListOfficeDetail.sampleOrderExpiredAt'),
            render: renderSampleOrderExpiredAt,
            cellStyle: {
                minWidth: 350,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
            },
        },
        contributeToOkagesamaNoMoris: {
            title: t('pages.offerList.okagesamaNoMoriStatuses.title'),
            render: renderContributeToOkagesamaNoMoris,
            customSort: sortContributeToOkagesamaNoMoris,
        },
        csvDownloadAction: actionColumn(renderCsvDownloadAction),
        orderAction: actionColumn(renderAdminOrderAction),
        forumAction: actionColumn(renderForumAction),
        officeNames: {
            field: 'officeNames',
            title: t('params.office.name'),
            filterComponent: renderOfficeFilter,
            cellStyle: {
                minWidth: 100,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
            },
        },
    };
}
export function useOfferColumns(role) {
    var t = useTranslation().t;
    var renderLink = React.useCallback(function (url) {
        if (!validURL(url))
            return url;
        return (React.createElement("a", { href: url, target: "_blank", rel: "noopener noreferrer", download: true }, React.createElement(ArchiveIcon, null)));
    }, []);
    var renderDownloadLink = React.useCallback(function (offer) { return renderLink(offer.downloadLink); }, [renderLink]);
    var renderStockAmount = React.useCallback(function (offer) {
        var currentStock = "".concat(offer.remainingQuantity, " / ").concat(offer.quantity, " ").concat(t('labels.stockUnit'));
        if (offer.offerList.status === 'draft' || offer.minOrder === null)
            return currentStock;
        return "".concat(currentStock, " (").concat(weightFormat(offer.minOrder), ")");
    }, [t]);
    var orderTypeValues = t('params.offer.orderTypes', {
        returnObjects: true,
    });
    var textStyle = {
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
    };
    var renderTypicaHandlingRates = React.useCallback(function (offer) {
        return (React.createElement(Box, null, Object.entries(offer.typicaHandlingRates).map(function (_a) {
            var key = _a[0], v = _a[1];
            return (React.createElement(Box, { key: key },
                t("params.offer.typicaHandlingRate.".concat(camelize(key))),
                ":",
                ' ',
                v || '-'));
        })));
    }, [t]);
    var renderEditTypicaHandlingRates = React.useCallback(function (props) {
        var typicaHandlingRates = props.value, onChange = props.onChange, otherProps = __rest(props, ["value", "onChange"]);
        if (!typicaHandlingRates)
            return React.createElement(React.Fragment, null); // on create
        var handleChange = function (event) {
            var _a;
            var _b = event.target, name = _b.name, value = _b.value;
            var nextValue = __assign(__assign({}, typicaHandlingRates), (_a = {}, _a[name] = value, _a));
            onChange(nextValue);
        };
        return (React.createElement(Box, null, Object.entries(typicaHandlingRates).map(function (_a) {
            var key = _a[0], v = _a[1];
            return (React.createElement(TextField, __assign({ key: key, name: key, type: "number", fullWidth: true, label: t("params.offer.typicaHandlingRate.".concat(camelize(key))), onChange: handleChange, value: v, style: { float: 'right' }, inputProps: {
                    style: {
                        textAlign: 'right',
                    },
                } }, otherProps)));
        })));
    }, [t]);
    var renderNarrativeUrls = React.useCallback(function (offer) {
        return (React.createElement(Box, null, Object.entries(offer.narrativeUrls).map(function (_a) {
            var key = _a[0], v = _a[1];
            return (React.createElement(Box, { key: key },
                key,
                ": ",
                v || '-'));
        })));
    }, []);
    var renderEditNarrativeUrls = React.useCallback(function (props) {
        var narrativeUrls = props.value, onChange = props.onChange, otherProps = __rest(props, ["value", "onChange"]);
        if (!narrativeUrls)
            return React.createElement(React.Fragment, null); // on create
        var handleChange = function (event) {
            var _a;
            var _b = event.target, name = _b.name, value = _b.value;
            var nextValue = __assign(__assign({}, narrativeUrls), (_a = {}, _a[name] = value, _a));
            onChange(nextValue);
        };
        return (React.createElement(Box, null, Object.entries(narrativeUrls).map(function (_a) {
            var key = _a[0], v = _a[1];
            return (React.createElement(TextField, __assign({ key: key, name: key, fullWidth: true, label: key, onChange: handleChange, value: v }, otherProps)));
        })));
    }, []);
    return {
        lotNumber: column('lotNumber', { defaultSort: 'asc', sorting: true }),
        lotRef: column('lotRef', role && role === 'producer' ? { title: t('labels.lotRef') } : {}),
        stockAmount: {
            title: t('params.offer.remainingQuantity'),
            render: renderStockAmount,
        },
        bagmark: column('bagmark'),
        originType: column('originType'),
        farm: column('farm'),
        producerName: column('producerName'),
        region: column('region'),
        district: column('district'),
        altitude: column('altitude'),
        farmSize: column('farmSize'),
        variety: column('variety'),
        grade: column('grade'),
        processing: column('processing'),
        processingMethod: column('processingMethod', { cellStyle: textStyle }),
        harvestYear: column('harvestYear', { editComponent: YearPicker }),
        harvestMonth: column('harvestMonth', { editComponent: MonthPicker }),
        minOrder: numericColumn('minOrder'),
        remainingQuantity: numericColumn('remainingQuantity'),
        totalSampleQuantity: numericColumn('totalSampleQuantity'),
        fobPrice: numericColumn('fobPrice'),
        bagSize: numericColumn('bagSize'),
        package: column('package', { lookup: packages }),
        breakdownFarm: numericColumn('breakdownFarm'),
        breakdownMill: numericColumn('breakdownMill'),
        breakdownProducer: numericColumn('breakdownProducer'),
        producerComment: column('producerComment', {
            cellStyle: { minWidth: 200, whiteSpace: 'pre' },
        }),
        downloadLink: column('downloadLink', {
            render: renderDownloadLink,
            cellStyle: { minWidth: 200 },
        }),
        purchaseLimit: numericColumn('purchaseLimit'),
        note: column('note', { cellStyle: textStyle }),
        orderType: column('orderType', { lookup: orderTypeValues }),
        ordersCount: numericColumn('ordersCount', {
            title: t('params.offerList.ordersCount'),
        }),
        ordersTotalQuantity: numericColumn('ordersTotalQuantity', {
            title: t('params.offerList.orderItemsTotalQuantity'),
        }),
        ordersTotalWeight: numericColumn('ordersTotalWeight', {
            title: t('params.offerList.orderItemsTotalWeight'),
        }),
        typicaStockTotalQuantity: numericColumn('typicaStockTotalQuantity'),
        typicaStockTotalWeight: numericColumn('typicaStockTotalWeight'),
        typicaHandlingRates: column('typicaHandlingRates', {
            render: renderTypicaHandlingRates,
            editComponent: renderEditTypicaHandlingRates,
            sorting: false,
        }),
        narrativeUrls: column('narrativeUrls', {
            render: renderNarrativeUrls,
            editComponent: renderEditNarrativeUrls,
            sorting: false,
        }),
    };
}
export function usePreOrderRequestGroupColumns(role) {
    var t = useTranslation().t;
    var renderOfferListLink = React.useCallback(function (data) { return (React.createElement(OfferListLinks, { offerLists: [data.offerList], pathPrefix: "/".concat(role) })); }, [role]);
    return {
        offerListName: {
            title: t('params.offerList.name'),
            render: renderOfferListLink,
            editable: 'never',
            sorting: false,
            filtering: false,
        },
        lotNumber: {
            title: t('params.offer.lotNumber'),
            field: 'lotNumber',
            editable: 'never',
            sorting: true,
            filtering: false,
        },
        processing: {
            title: t('params.offer.processing'),
            field: 'processing',
            editable: 'never',
            sorting: true,
            filtering: false,
        },
        variety: {
            title: t('params.offer.variety'),
            field: 'variety',
            editable: 'never',
            sorting: true,
            filtering: false,
        },
        totalWeight: numericColumn('totalWeight', {
            title: t('params.offer.preOrderRequestTotalWeight'),
            editable: 'never',
            sorting: false,
            filtering: false,
        }),
    };
}
export function useOrderColumns(_a) {
    var role = _a.role, filters = _a.filters, orderBy = _a.orderBy, orderDirection = _a.orderDirection;
    var t = useTranslation().t;
    var statusItems = useOrderListItem().statusItems;
    var paymentTimingForPriceVersion = usePaymentTimingForPriceVersion();
    var statusOptions = toOptions(statusItems);
    var renderOfferListLinks = React.useCallback(function (order) { return (React.createElement(OfferListLinks, { offerLists: order.offerLists, pathPrefix: "/".concat(role) })); }, [role]);
    var renderPaymentTiming = React.useCallback(function (order) { return paymentTimingForPriceVersion(order); }, [paymentTimingForPriceVersion]);
    var renderProducerNames = React.useCallback(function (order) {
        var _a = order.offerLists, offerLists = _a === void 0 ? [] : _a;
        var producerNames = offerLists
            .map(function (_a) {
            var _b = _a.producer, firstname = _b.firstname, lastname = _b.lastname;
            return "".concat(firstname, " ").concat(lastname);
        })
            .join(', ');
        return React.createElement("span", null, producerNames);
    }, []);
    var renderRoasterName = React.useCallback(function (order) {
        var _a = order.roaster, firstname = _a.firstname, lastname = _a.lastname;
        return React.createElement("span", null, t('fullname', { firstname: firstname, lastname: lastname }));
    }, [t]);
    var defaultSort = function (field) {
        if (orderBy === field &&
            (orderDirection === 'asc' || orderDirection === 'desc')) {
            return orderDirection;
        }
        return;
    };
    var renderStatus = React.useCallback(function (order) {
        return React.createElement(OrderStatusTag, { order: order });
    }, []);
    return {
        offerListLink: {
            title: t('params.offerList.name'),
            render: renderOfferListLinks,
            sorting: false,
            filtering: false,
        },
        producerName: {
            title: t('labels.curatorName'),
            render: renderProducerNames,
            sorting: false,
            filtering: false,
        },
        roasterName: {
            title: t('params.roaster.name'),
            render: renderRoasterName,
            sorting: false,
            filtering: false,
        },
        billingName: column('billingName', {
            filtering: false,
            defaultSort: defaultSort('billingName'),
        }),
        storeName: column('storeName', {
            filtering: false,
            defaultSort: defaultSort('storeName'),
        }),
        personName: column('personName', {
            filtering: false,
            defaultSort: defaultSort('personName'),
        }),
        nameKana: column('nameKana', {
            filtering: false,
            defaultSort: defaultSort('nameKana'),
        }),
        phone: column('phone', {
            filtering: false,
            defaultSort: defaultSort('phone'),
        }),
        email: column('email', {
            filtering: false,
            defaultSort: defaultSort('email'),
        }),
        postalCode: column('postalCode', {
            filtering: false,
            defaultSort: defaultSort('postalCode'),
        }),
        prefecture: column('prefecture', {
            filtering: false,
            defaultSort: defaultSort('prefecture'),
        }),
        city: column('city', {
            filtering: false,
            defaultSort: defaultSort('city'),
        }),
        address: column('address', {
            filtering: false,
            defaultSort: defaultSort('address'),
        }),
        building: column('building', {
            filtering: false,
            defaultSort: defaultSort('building'),
        }),
        paymentTiming: column('paymentTiming', {
            filtering: true,
            render: renderPaymentTiming,
            filterCellStyle: {
                maxWidth: 180,
            },
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.paymentTiming,
            defaultSort: defaultSort('paymentTiming'),
        }),
        status: column('status', {
            filtering: true,
            lookup: statusOptions,
            render: renderStatus,
            filterCellStyle: {
                maxWidth: 180,
            },
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.status,
            defaultSort: defaultSort('status'),
        }),
        createdAt: datetimeColumn('createdAt', {
            filtering: false,
            defaultSort: defaultSort('createdAt'),
        }),
    };
}
export function useOrderItemColumns(_a) {
    var role = _a.role, currency = _a.currency;
    var t = useTranslation().t;
    var renderLotNumber = React.useCallback(function (param) {
        var editableOrderItem = param;
        if (editableOrderItem.lotNumber) {
            return React.createElement("span", null, editableOrderItem.lotNumber);
        }
        var orderItem = param;
        if (orderItem.offer) {
            return React.createElement("span", null, orderItem.offer.lotNumber);
        }
        return React.createElement("span", null);
    }, []);
    var renderTransportStatus = React.useCallback(function (params) {
        var _a = params, transportStatus = _a.transportStatus, status = _a.order.status;
        if (status === 'canceled')
            return '-';
        return (React.createElement(TransportToWarehouseStatus, { transportToWarehouseStatus: transportStatus }));
    }, []);
    var renderTransportToWarehouseNote = React.useCallback(function (params) {
        var _a = params, transportToWarehouse = _a.transportToWarehouse, status = _a.order.status;
        if (status === 'canceled')
            return '-';
        if (!transportToWarehouse.note)
            return '-';
        return React.createElement(Alert, { severity: "info" }, transportToWarehouse.note);
    }, []);
    var renderTransportToWarehouseTrackingUrl = React.useCallback(function (params) {
        var _a = params, transportToWarehouse = _a.transportToWarehouse, status = _a.order.status;
        if (status === 'canceled')
            return '-';
        if (!transportToWarehouse.trackingUrl)
            return '-';
        return (React.createElement("a", { href: transportToWarehouse.trackingUrl, target: "_blank", rel: "noopener noreferrer" }, transportToWarehouse.trackingUrl));
    }, []);
    var renderQuantityWithWeight = React.useCallback(function (params) {
        var _a = params, isTypicaStockOrder = _a.order.isTypicaStockOrder, quantity = _a.quantity, _b = _a.offer, minOrder = _b.minOrder, bagSize = _b.bagSize;
        var weight = isTypicaStockOrder
            ? weightFormat(parseFloat(bagSize) * quantity)
            : weightFormat(parseFloat(minOrder) * quantity);
        return "".concat(quantity, " (").concat(weight, ")");
    }, []);
    var renderRoasterWarehouseItemsLink = React.useCallback(function (params) {
        var _a = params, lotNumber = _a.offer.lotNumber, transportStatus = _a.transportStatus, status = _a.order.status;
        if (status === 'canceled' || transportStatus !== 'warehousing')
            return null;
        var query = {
            filters: {
                'offer.lotNumber': lotNumber,
            },
        };
        var path = "/roaster/warehouse_items?".concat(qs.stringify(query));
        return (React.createElement(Link, { to: path },
            React.createElement(DeliveryRequestIcon, null)));
    }, []);
    var renderPreOrderRequest = React.useCallback(function (param) {
        var orderItem = param;
        return orderItem.isPreOrderRequested ? (React.createElement("span", null,
            React.createElement(CheckIcon, { style: { color: 'green' } }))) : (React.createElement("span", null, role === 'roaster' ? (React.createElement(Link, { to: "/roaster/pre_order_requests" }, t('pages.orderDetail.preOrderRequestCreateOrEdit'))) : ('-')));
    }, [t, role]);
    var renderSentPreOrderMail = React.useCallback(function (param) {
        var orderItem = param;
        return orderItem.isSentPreOrderMail ? (React.createElement("span", null,
            React.createElement(CheckIcon, { style: { color: 'green' } }))) : (React.createElement("span", null, '-'));
    }, []);
    return {
        lotNumber: {
            title: t('params.offer.lotNumber'),
            editable: 'never',
            render: renderLotNumber,
        },
        transportStatus: {
            title: t('params.transportToWarehouse.status'),
            editable: 'never',
            render: renderTransportStatus,
        },
        transportToWarehouseNote: {
            title: t('params.transportToWarehouse.note'),
            editable: 'never',
            render: renderTransportToWarehouseNote,
            cellStyle: {
                minWidth: 220,
            },
        },
        transportToWarehouseTrackingUrl: {
            title: t('params.transportToWarehouse.trackingUrl'),
            editable: 'never',
            render: renderTransportToWarehouseTrackingUrl,
        },
        quantity: numericColumn('quantity', {
            title: t('params.orderItem.quantity'),
        }),
        quantityWithWeight: {
            title: t('params.orderItem.quantityWithWeight'),
            editable: 'never',
            render: renderQuantityWithWeight,
        },
        comment: column('comment', {
            title: t('params.orderItem.comment'),
            cellStyle: {
                minWidth: 200,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
            },
        }),
        roasterWarehouseItemsLink: {
            title: t('pages.warehouseItems.deliveryRequest'),
            editable: 'never',
            render: renderRoasterWarehouseItemsLink,
        },
        isPreOrderRequested: {
            title: t('params.orderItem.preOrderRequest'),
            editable: 'never',
            render: renderPreOrderRequest,
        },
        isSentPreOrderMail: {
            title: t('params.orderItem.sentPreOrderMail'),
            editable: 'never',
            render: renderSentPreOrderMail,
        },
        subTotalDiscount: moneyColumn('subTotalDiscount', {
            title: t('params.orderItem.subTotalDiscount'),
            getCurrency: function () { return currency; },
        }),
        typicaHandlingFeeDiscount: moneyColumn('typicaHandlingFeeDiscount', {
            title: t('params.orderItem.typicaHandlingFeeDiscount'),
            getCurrency: function () { return currency; },
        }),
        totalFeeDiscount: moneyColumn('totalFeeDiscount', {
            title: t('params.orderItem.totalFeeDiscount'),
            getCurrency: function () { return currency; },
        }),
    };
}
export function useSampleOrderColumns(_a) {
    var role = _a.role, filters = _a.filters, orderBy = _a.orderBy, orderDirection = _a.orderDirection;
    var t = useTranslation().t;
    var statusOptions = t('params.sampleOrder.statuses', {
        returnObjects: true,
    });
    var renderOfferListLinks = React.useCallback(function (sampleOrder) {
        var offerLists = sampleOrder.offerLists;
        return React.createElement(OfferListLinks, { offerLists: offerLists, pathPrefix: "/".concat(role) });
    }, [role]);
    var renderProducerNames = React.useCallback(function (order) {
        var offerLists = order.offerLists;
        return offerLists.map(function (_a) {
            var _b = _a.producer, id = _b.id, firstname = _b.firstname, lastname = _b.lastname;
            return (React.createElement("div", { key: id }, "".concat(firstname, " ").concat(lastname)));
        });
    }, []);
    var renderRoasterName = React.useCallback(function (order) {
        var _a = order.roaster, firstname = _a.firstname, lastname = _a.lastname;
        return React.createElement("span", null, t('fullname', { firstname: firstname, lastname: lastname }));
    }, [t]);
    var renderDeliveryCompany = React.useCallback(function (sampleOrder) {
        if (!sampleOrder.trackingUrl)
            return React.createElement("span", null, sampleOrder.deliveryCompany);
        return (React.createElement("a", { href: sampleOrder.trackingUrl, target: "_blank", rel: "noopener noreferrer" }, sampleOrder.deliveryCompany));
    }, []);
    var defaultSort = function (field) {
        if (orderBy === field &&
            (orderDirection === 'asc' || orderDirection === 'desc')) {
            return orderDirection;
        }
        return;
    };
    return {
        offerListLink: {
            title: t('params.offerList.name'),
            render: renderOfferListLinks,
            sorting: false,
            filtering: false,
        },
        roasterName: {
            title: t('params.roaster.name'),
            render: renderRoasterName,
            sorting: false,
            filtering: false,
        },
        producerName: {
            title: t('labels.curatorName'),
            render: renderProducerNames,
            sorting: false,
            filtering: false,
        },
        offerListName: column('offerList.name'),
        storeName: column('storeName', {
            filtering: false,
            defaultSort: defaultSort('storeName'),
        }),
        personName: column('personName', {
            filtering: false,
            defaultSort: defaultSort('personName'),
        }),
        nameKana: column('nameKana', {
            filtering: false,
            defaultSort: defaultSort('nameKana'),
        }),
        phone: column('phone', {
            filtering: false,
            defaultSort: defaultSort('phone'),
        }),
        email: column('email', {
            filtering: false,
            defaultSort: defaultSort('email'),
        }),
        postalCode: column('postalCode', {
            filtering: false,
            defaultSort: defaultSort('postalCode'),
        }),
        prefecture: column('prefecture', {
            filtering: false,
            defaultSort: defaultSort('prefecture'),
        }),
        city: column('city', {
            filtering: false,
            defaultSort: defaultSort('city'),
        }),
        address: column('address', {
            filtering: false,
            defaultSort: defaultSort('address'),
        }),
        building: column('building', {
            filtering: false,
            defaultSort: defaultSort('building'),
        }),
        status: column('status', {
            filtering: true,
            lookup: statusOptions,
            filterCellStyle: {
                maxWidth: 180,
            },
            defaultFilter: filters === null || filters === void 0 ? void 0 : filters.status,
            defaultSort: defaultSort('status'),
        }),
        deliveryCompany: column('deliveryCompany', {
            render: renderDeliveryCompany,
            filtering: false,
            defaultSort: defaultSort('deliveryCompany'),
        }),
        trackingNumber: column('trackingNumber', {
            filtering: false,
            defaultSort: defaultSort('trackingNumber'),
        }),
        createdAt: datetimeColumn('createdAt', {
            filtering: false,
            defaultSort: defaultSort('createdAt'),
        }),
    };
}
export function useOrderPriceItemColumns() {
    var t = useTranslation().t;
    return {
        lotNumber: {
            field: 'orderItem.offer.lotNumber',
            title: t('params.offer.lotNumber'),
            editable: 'never',
            cellStyle: {
                whiteSpace: 'nowrap',
            },
        },
        quantity: numericColumn('quantity'),
        fobPrice: numericColumn('fobPrice', { title: t('params.offer.fobPrice') }),
        weight: numericColumn('weight'),
        subTotal: numericColumn('subTotal'),
        shippingFee: numericColumn('shippingFee'),
        miscFee: numericColumn('miscFee'),
        taxedMiscFee: numericColumn('taxedMiscFee'),
        importTax: numericColumn('importTax'),
        typicaHandlingFee: numericColumn('typicaHandlingFee'),
        paymentTimingHandlingFee: numericColumn('paymentTimingHandlingFee'),
        consumptionTax: numericColumn('consumptionTax'),
        serviceFee: numericColumn('serviceFee'),
        totalFee: numericColumn('totalFee', { editable: 'never' }),
        feePer1kg: numericColumn('feePer1kg', { editable: 'never' }),
    };
}
export function useInvoiceColumns() {
    var t = useTranslation().t;
    var paymentMethodItems = useInvoiceListItem().paymentMethodItems;
    var paymentTimingForPriceVersion = usePaymentTimingForPriceVersion();
    var renderPaymentTimings = React.useCallback(function (invoice) {
        if (invoice.orders.length === 0)
            return null;
        return invoice.orders
            .map(function (order) { return paymentTimingForPriceVersion(order); })
            .join(', ');
    }, [paymentTimingForPriceVersion]);
    var renderPaid = React.useCallback(function (invoice) {
        if (!invoice.paidAt)
            return null;
        return React.createElement(CheckIcon, { style: { color: 'green' } });
    }, []);
    var renderTaiwanApiResponse = React.useCallback(function (invoice) {
        var _a, _b;
        if (!((_a = invoice.taiwanApiResponse) === null || _a === void 0 ? void 0 : _a.resultCode))
            return null;
        if (((_b = invoice.taiwanApiResponse) === null || _b === void 0 ? void 0 : _b.resultCode) === 'success') {
            return invoice.taiwanApiResponse.receiptNumber;
        }
        else {
            return React.createElement(ErrorIcon, { style: { color: 'red' } });
        }
    }, []);
    return {
        billingName: {
            title: t('params.invoice.billingName'),
            field: 'billingName',
        },
        storeName: {
            title: t('params.invoice.storeName'),
            field: 'storeName',
        },
        personName: {
            title: t('params.invoice.personName'),
            field: 'personName',
        },
        paymentTimings: {
            title: t('params.order.paymentTiming'),
            field: 'paymentTimings',
            render: renderPaymentTimings,
            sorting: false,
        },
        paymentMethod: column('paymentMethod', {
            title: t('params.order.paymentMethod'),
            lookup: toOptions(paymentMethodItems),
        }),
        total: moneyColumn('total', { getCurrency: function (invoice) { return invoice.currency; } }),
        createdDate: column('createdDate'),
        expiredDate: column('expiredDate'),
        taxStatementCreatedDate: column('taxStatementCreatedDate'),
        createdAt: datetimeColumn('createdAt', { defaultSort: 'desc' }),
        paid: {
            field: 'paidAt',
            title: t('params.invoice.paid'),
            sorting: false,
            align: 'center',
            render: renderPaid,
        },
        title: column('title'),
        taiwanApiResponseResult: {
            field: 'taiwanApiResponse',
            title: t('params.invoice.taiwanApiResponse'),
            align: 'center',
            render: renderTaiwanApiResponse,
        },
        communityManager: column('roaster.communityManager', {
            render: function (_a) {
                var _b;
                var roaster = _a.roaster;
                return (_b = roaster.communityManager) === null || _b === void 0 ? void 0 : _b.name;
            },
        }),
        office: column('roaster.country.office.id', {
            render: function (_a) {
                var roaster = _a.roaster;
                return roaster.country.office.name;
            },
        }),
    };
}
export var useInvoiceItemColumns = function (_a) {
    var taxName = _a.taxName;
    var renderWeight = React.useCallback(function (invoiceItem) {
        return weightFormat(invoiceItem.weight);
    }, []);
    return {
        description: column('description'),
        weight: numericColumn('weight', { render: renderWeight }),
        beansAmount: function (currency) {
            return moneyColumn('beansAmount', {
                getCurrency: function () { return currency; },
            });
        },
        typicaHandlingAmount: function (currency) {
            return moneyColumn('typicaHandlingAmount', {
                getCurrency: function () { return currency; },
            });
        },
        taxedMiscAmount: function (currency) {
            return moneyColumn('taxedMiscAmount', {
                getCurrency: function () { return currency; },
            });
        },
        nonTaxedMiscAmount: function (currency) {
            return moneyColumn('nonTaxedMiscAmount', {
                getCurrency: function () { return currency; },
            });
        },
        tax: function (currency) {
            return moneyColumn('tax', {
                title: taxName,
                getCurrency: function () { return currency; },
            });
        },
        subTotal: function (currency) {
            return moneyColumn('subTotal', {
                getCurrency: function () { return currency; },
            });
        },
        transferDate: dateColumn('transferDate', { emptyValue: '-' }),
    };
};
export function useCountryInformationColumns() {
    var t = useTranslation().t;
    var countryMap = {};
    TradeCounties.forEach(function (code) { return (countryMap[code] = t("countries.".concat(code))); });
    return {
        country: column('country', { lookup: countryMap }),
        movieUrl: column('storyUrl'),
        storyUrl: column('movieUrl'),
    };
}
export function useWarehouseItemColumns(_a) {
    var role = _a.role;
    var t = useTranslation().t;
    var paymentTimingForPriceVersion = usePaymentTimingForPriceVersion();
    var renderOfferListLink = React.useCallback(function (_a) {
        var offerList = _a.offer.offerList;
        var country = t("countries.".concat(offerList.producer.country), {
            lng: 'en',
        });
        return (React.createElement(Link, { to: "/".concat(role, "/offer_lists/").concat(offerList.id) },
            country,
            " ",
            offerList.name));
    }, [t, role]);
    var renderOrderLink = React.useCallback(function (_a) {
        var id = _a.order.id;
        return React.createElement(Link, { to: "/".concat(role, "/orders/").concat(id) },
            "#",
            id);
    }, [role]);
    var renderLotNumber = React.useCallback(function (_a) {
        var _b = _a.offer, lotNumber = _b.lotNumber, bagSize = _b.bagSize, processing = _b.processing;
        return (React.createElement("span", { style: { whiteSpace: 'pre-wrap' } },
            lotNumber,
            '\n',
            "(",
            processing,
            " ",
            weightFormat(bagSize),
            ")"));
    }, []);
    var renderStockFilter = React.useCallback(function (props) { return React.createElement(StockFilter, __assign({}, props)); }, []);
    var renderRoaster = React.useCallback(function (_a) {
        var roaster = _a.order.roaster;
        var companyAndStoreName = "".concat(roaster.company || '', " ").concat(roaster.storeName || '');
        var fullname = t('fullname', roaster);
        if (role !== 'admin') {
            return React.createElement(React.Fragment, null);
        }
        return (React.createElement(Link, { to: "/admin/roasters/".concat(roaster.id) },
            "#",
            roaster.id,
            " ",
            companyAndStoreName,
            " ",
            fullname));
    }, [t, role]);
    var renderRoasterFilter = React.useCallback(function (props) { return React.createElement(RoasterFilter, __assign({}, props)); }, []);
    var renderDateRangeFilter = React.useCallback(function (props) { return React.createElement(DateRangeFilter, __assign({}, props)); }, []);
    var renderPaymentTiming = React.useCallback(function (_a) {
        var order = _a.order;
        return paymentTimingForPriceVersion(order);
    }, [paymentTimingForPriceVersion]);
    return {
        id: {
            field: 'id',
            title: t('params.id'),
            editable: 'never',
        },
        offerListLink: {
            field: 'offer.offerList.name',
            title: t('params.offerList.name'),
            render: renderOfferListLink,
            sorting: false,
            editable: 'never',
        },
        orderLink: {
            field: 'order.id',
            title: t('params.order.id'),
            render: renderOrderLink,
            editable: 'never',
            sorting: false,
        },
        lotNumber: {
            field: 'offer.lotNumber',
            title: t('params.offer.lotNumber'),
            render: renderLotNumber,
            editable: 'never',
        },
        warehouse: {
            field: 'warehouse.name',
            title: t('params.warehouse.name'),
            editable: 'never',
        },
        roaster: {
            field: 'order.roaster',
            title: t('params.roasterId'),
            editable: 'never',
            render: renderRoaster,
            filterComponent: renderRoasterFilter,
            filterCellStyle: {
                minWidth: 250,
            },
        },
        total: numericColumn('total', {
            editable: 'never',
        }),
        remainingQuantity: numericColumn('remainingQuantity', {
            editable: 'never',
            filterComponent: renderStockFilter,
        }),
        deliveredQuantity: numericColumn('deliveredQuantity', {
            editable: 'never',
        }),
        deliveringQuantity: numericColumn('deliveringQuantity', {
            editable: 'never',
        }),
        requestableQuantity: numericColumn('requestableQuantity', {
            editable: 'never',
            filterComponent: renderStockFilter,
        }),
        exportedAt: datetimeColumn('exportedAt', {
            filterComponent: renderDateRangeFilter,
            editComponent: EditDateTimePicker,
        }),
        importedAt: datetimeColumn('importedAt', {
            filterComponent: renderDateRangeFilter,
            editComponent: EditDateTimePicker,
        }),
        endOfStoragePeriod: datetimeColumn('endOfStoragePeriod', {
            filterComponent: renderDateRangeFilter,
            editComponent: EditDateTimePicker,
        }),
        isTypicaSelection: booleanColumn('isTypicaSelection', {
            editable: 'never',
        }),
        paymentTiming: {
            title: t('params.order.paymentTiming'),
            field: 'order.paymentTiming',
            render: renderPaymentTiming,
            sorting: false,
        },
    };
}
export function useDeliveryRequestColumns() {
    var t = useTranslation().t;
    var renderNameWithKana = React.useCallback(function (deliveryRequest) {
        var personName = deliveryRequest.personName, nameKana = deliveryRequest.nameKana;
        if (nameKana)
            return "".concat(personName, " (").concat(nameKana, ")");
        return personName;
    }, []);
    var renderFullAddress = React.useCallback(function (deliveryRequest) {
        var prefecture = deliveryRequest.prefecture, city = deliveryRequest.city, address = deliveryRequest.address, building = deliveryRequest.building;
        return "".concat(prefecture, " ").concat(city, " ").concat(address, " ").concat(building || '');
    }, []);
    var renderDeliveryItems = React.useCallback(function (deliveryRequest) { return (React.createElement(DeliveryRequestItemList, { deliveryRequest: deliveryRequest })); }, []);
    var renderStatus = React.useCallback(function (deliveryRequest) {
        return React.createElement(DeliveryRequestStatusTag, { deliveryRequest: deliveryRequest });
    }, []);
    return {
        id: column('id'),
        roasterCompany: {
            field: 'roaster.company',
            title: t('params.roaster.company'),
        },
        roasterStoreName: {
            field: 'roaster.storeName',
            title: t('params.roaster.storeName'),
        },
        roasterFirstname: {
            field: 'roaster.firstname',
            title: t('params.roaster.firstname'),
        },
        roasterLastname: {
            field: 'roaster.lastname',
            title: t('params.roaster.lastname'),
        },
        storeName: {
            field: 'storeName',
            title: t('params.order.storeName'),
        },
        nameWithKana: {
            field: 'personName',
            title: t('params.deliveryRequest.personName'),
            render: renderNameWithKana,
        },
        phone: {
            field: 'phone',
            title: t('params.deliveryRequest.phone'),
        },
        countryCallingCode: {
            field: 'countryCallingCode',
            title: t('params.deliveryRequest.countryCallingCode'),
        },
        postalCode: {
            field: 'postalCode',
            title: t('params.deliveryRequest.postalCode'),
        },
        fullAddress: {
            field: 'prefecture',
            title: t('labels.fullAddress'),
            render: renderFullAddress,
            sorting: false,
        },
        deliveryItems: {
            title: t('labels.deliveryItems'),
            render: renderDeliveryItems,
            sorting: false,
        },
        status: column('status', {
            lookup: t('params.deliveryRequest.statuses', {
                returnObjects: true,
            }),
            render: renderStatus,
        }),
        createdAt: datetimeColumn('createdAt'),
        updatedAt: datetimeColumn('updatedAt'),
        requestDate: column('requestDate', {
            type: 'date',
            emptyValue: t('labels.fastestDeliveryRequest'),
            cellStyle: {
                whiteSpace: 'nowrap',
            },
        }),
    };
}
export function useTransportToWarehouseColumns() {
    var t = useTranslation().t;
    var renderWarehouse = React.useCallback(function (data) {
        var _a;
        return ((_a = data.warehouse) === null || _a === void 0 ? void 0 : _a.name) || '';
    }, []);
    var statusOptions = React.useMemo(function () {
        return StatusValues.reduce(function (result, status) {
            result[status] = t("params.transportToWarehouse.statuses.".concat(camelize(status)));
            return result;
        }, {});
    }, [t]);
    var unitPriceColumn = React.useCallback(function (field) {
        return moneyColumn(field, {
            getCurrency: function (transportToWarehouse) { return transportToWarehouse.currency; },
            title: t("params.transportToWarehouse.".concat(field)),
        });
    }, [t]);
    var renderImportMoistureContent = React.useCallback(function (transportToWarehouse) {
        if (!transportToWarehouse.importMoistureContent)
            return '';
        return formatPercentage(transportToWarehouse.importMoistureContent);
    }, []);
    var renderImportWaterActivity = React.useCallback(function (transportToWarehouse) {
        if (!transportToWarehouse.importWaterActivity)
            return '';
        return formatWaterActivity(transportToWarehouse.importWaterActivity);
    }, []);
    var renderExportMoistureContent = React.useCallback(function (transportToWarehouse) {
        if (!transportToWarehouse.exportMoistureContent)
            return '';
        return formatPercentage(transportToWarehouse.exportMoistureContent);
    }, []);
    var renderExportWaterActivity = React.useCallback(function (transportToWarehouse) {
        if (!transportToWarehouse.exportWaterActivity)
            return '';
        return formatWaterActivity(transportToWarehouse.exportWaterActivity);
    }, []);
    var renderImportTaxRate = React.useCallback(function (transportToWarehouse) {
        if (!transportToWarehouse.importTaxRate)
            return '';
        return formatPercentage(transportToWarehouse.importTaxRate);
    }, []);
    var renderEditMoistureContent = React.useCallback(function (props) { return (React.createElement(NumericTextField, __assign({}, props, { inputProps: { max: 99.9, min: 0, step: 0.1, maxLength: 4 } }))); }, []);
    var renderEditWaterActivity = React.useCallback(function (props) { return (React.createElement(NumericTextField, __assign({}, props, { inputProps: { max: 0.99, min: 0, step: 0.01, maxLength: 4 } }))); }, []);
    var renderEditImportTaxRate = React.useCallback(function (props) { return (React.createElement(NumericTextField, __assign({}, props, { inputProps: { max: 99.9, min: 0, step: 0.1, maxLength: 3 } }))); }, []);
    var estimatedDeliveryPeriodMonthPartOptions = React.useMemo(function () {
        return EstimatedDeliveryPeriodMonthParts.reduce(function (result, part) {
            result[part] = t("params.transportToWarehouse.estimatedDeliveryPeriodMonthParts.".concat(part));
            return result;
        }, {});
    }, [t]);
    return {
        warehouseName: {
            field: 'warehouse',
            title: t('params.warehouse.name'),
            render: renderWarehouse,
            editable: 'never',
        },
        status: {
            field: 'status',
            title: t('params.transportToWarehouse.status'),
            lookup: statusOptions,
        },
        exportedAt: datetimeColumn('exportedAt'),
        importedAt: datetimeColumn('importedAt'),
        ordersCount: numericColumn('ordersCount', {
            title: t('params.offerList.ordersCount'),
            editable: 'never',
        }),
        ordersTotalQuantity: numericColumn('ordersTotalQuantity', {
            title: t('params.offerList.orderItemsTotalQuantity'),
            editable: 'never',
        }),
        ordersTotalWeight: numericColumn('ordersTotalWeight', {
            title: t('params.offerList.orderItemsTotalWeight'),
            editable: 'never',
        }),
        typicaStockTotalQuantity: numericColumn('typicaStockTotalQuantity', {
            editable: 'never',
        }),
        typicaStockTotalWeight: numericColumn('typicaStockTotalWeight', {
            editable: 'never',
        }),
        note: column('note'),
        trackingUrl: column('trackingUrl'),
        simulationShippingFeeUnitPrice: unitPriceColumn('simulationShippingFeeUnitPrice'),
        simulationMiscFeeUnitPrice: unitPriceColumn('simulationMiscFeeUnitPrice'),
        simulationTaxedMiscFeeUnitPrice: unitPriceColumn('simulationTaxedMiscFeeUnitPrice'),
        fixedShippingFeeUnitPrice: unitPriceColumn('fixedShippingFeeUnitPrice'),
        fixedMiscFeeUnitPrice: unitPriceColumn('fixedMiscFeeUnitPrice'),
        fixedTaxedMiscFeeUnitPrice: unitPriceColumn('fixedTaxedMiscFeeUnitPrice'),
        importMoistureContent: numericColumn('importMoistureContent', {
            render: renderImportMoistureContent,
            editComponent: renderEditMoistureContent,
        }),
        importWaterActivity: numericColumn('importWaterActivity', {
            render: renderImportWaterActivity,
            editComponent: renderEditWaterActivity,
        }),
        exportMoistureContent: numericColumn('exportMoistureContent', {
            render: renderExportMoistureContent,
            editComponent: renderEditMoistureContent,
        }),
        exportWaterActivity: numericColumn('exportWaterActivity', {
            render: renderExportWaterActivity,
            editComponent: renderEditWaterActivity,
        }),
        estimatedDeliveryPeriodYear: numericColumn('estimatedDeliveryPeriodYear', {
            editComponent: YearPicker,
        }),
        estimatedDeliveryPeriodMonth: numericColumn('estimatedDeliveryPeriodMonth', {
            editComponent: MonthPicker,
        }),
        estimatedDeliveryPeriodMonthPart: numericColumn('estimatedDeliveryPeriodMonthPart', {
            lookup: estimatedDeliveryPeriodMonthPartOptions,
        }),
        beansPaidAt: column('beansPaidAt', {
            render: function (ttw) {
                return formatDateTimeWithOffset(ttw.beansPaidAt);
            },
            editComponent: EditDateTimePickerWithTimeZone,
            cellStyle: {
                minWidth: 250,
            },
        }),
        miscFeesPaidAt: column('miscFeesPaidAt', {
            render: function (ttw) {
                return formatDateTimeWithOffset(ttw.miscFeesPaidAt);
            },
            editComponent: EditDateTimePickerWithTimeZone,
            cellStyle: {
                minWidth: 250,
            },
        }),
        shippingFeePaidAt: column('shippingFeePaidAt', {
            render: function (ttw) {
                return formatDateTimeWithOffset(ttw.shippingFeePaidAt);
            },
            editComponent: EditDateTimePickerWithTimeZone,
            cellStyle: {
                minWidth: 250,
            },
        }),
        importTaxPaidAt: column('importTaxPaidAt', {
            render: function (ttw) {
                return formatDateTimeWithOffset(ttw.importTaxPaidAt);
            },
            editComponent: EditDateTimePickerWithTimeZone,
            cellStyle: {
                minWidth: 250,
            },
        }),
        importTaxRate: numericColumn('importTaxRate', {
            render: renderImportTaxRate,
            editComponent: renderEditImportTaxRate,
        }),
    };
}
export function isBidEditable(row) {
    return row.status === 'active';
}
export function useBidActions(_a) {
    var role = _a.role;
    var navigate = useNavigate();
    var moveToAuctionPage = React.useCallback(function (_, bidAttributes) {
        var _a, _b, _c;
        var bid = bidAttributes;
        var editableBid = bidAttributes;
        var offerId = ((_a = bid.offer) === null || _a === void 0 ? void 0 : _a.id) || editableBid.offerId;
        var offerListId = ((_c = (_b = bid.offer) === null || _b === void 0 ? void 0 : _b.offerList) === null || _c === void 0 ? void 0 : _c.id) || editableBid.offerListId;
        navigate("/".concat(role, "/offer_lists/").concat(offerListId, "/auctions/").concat(offerId));
    }, [role, navigate]);
    return {
        linkToAuction: function (bid) {
            var offer = bid.offer;
            return {
                icon: function () { return React.createElement(AuctionIcon, null); },
                hidden: role !== 'admin' && !offer.auctionClosed,
                onClick: moveToAuctionPage,
            };
        },
    };
}
export function useBidColumns(_a) {
    var linkPrefix = _a.linkPrefix;
    var t = useTranslation().t;
    var editable = React.useCallback(function (_, bid) {
        return bid.status === 'active';
    }, []);
    var renderOfferListLink = React.useCallback(function (param) {
        var _a, _b;
        var bid = param;
        if (!((_b = (_a = bid.offer) === null || _a === void 0 ? void 0 : _a.offerList) === null || _b === void 0 ? void 0 : _b.name))
            return null;
        return (React.createElement(Link, { to: "".concat(linkPrefix, "/offer_lists/").concat(bid.offer.offerList.id) }, bid.offer.offerList.name));
    }, [linkPrefix]);
    var renderRoasterLink = React.useCallback(function (param) {
        var bid = param;
        if (!bid.order)
            return null;
        return (React.createElement(Link, { to: "".concat(linkPrefix, "/roasters/").concat(bid.order.roaster.id) }, t('fullname', bid.order.roaster)));
    }, [t, linkPrefix]);
    var renderStatus = React.useCallback(function (bid) { return React.createElement(BidStatusTag, { bid: bid }); }, []);
    var renderLotNumber = React.useCallback(function (param) {
        var editableBid = param;
        if (editableBid.lotNumber) {
            return React.createElement("span", null, editableBid.lotNumber);
        }
        var bid = param;
        if (bid.offer) {
            return React.createElement("span", null, bid.offer.lotNumber);
        }
        return React.createElement("span", null);
    }, []);
    var renderQuantityWithWeight = React.useCallback(function (params) {
        var _a = params, isTypicaStockOrder = _a.order.isTypicaStockOrder, quantity = _a.quantity, _b = _a.offer, minOrder = _b.minOrder, bagSize = _b.bagSize;
        var weight = isTypicaStockOrder
            ? weightFormat(parseFloat(bagSize) * quantity)
            : weightFormat(parseFloat(minOrder) * quantity);
        return "".concat(quantity, " (").concat(weight, ")");
    }, []);
    return {
        offerListLink: {
            title: t('params.offerList.name'),
            render: renderOfferListLink,
            editable: 'never',
        },
        roasterLink: column('order.roaster', {
            title: t('params.roaster.name'),
            render: renderRoasterLink,
            editable: 'never',
        }),
        lotNumber: {
            title: t('params.offer.lotNumber'),
            render: renderLotNumber,
            editable: 'never',
        },
        roasterFilteredName: column('roasterFilteredName', {
            title: t('params.roaster.name'),
        }),
        status: column('status', { render: renderStatus, editable: 'never' }),
        fobPrice: numericColumn('fobPrice', { editable: editable }),
        fobPriceUsdPerLb: numericColumn('fobPriceUsdPerLb', { editable: editable }),
        quantity: numericColumn('quantity', { editable: editable }),
        quantityWithWeight: {
            title: t('params.bid.quantityWithWeight'),
            editable: 'never',
            render: renderQuantityWithWeight,
        },
        bidAt: datetimeColumn('bidAt', { editable: 'never' }),
        buyPartial: booleanColumn('buyPartial', { editable: editable }),
        wonQuantity: numericColumn('wonQuantity'),
    };
}
export function usePreOrderRequestColumn(role) {
    var t = useTranslation().t;
    var _a = usePreOrderRequestItem(), processingItems = _a.processingItems, varietyItems = _a.varietyItems;
    var renderOfferListLink = React.useCallback(function (data) { return (React.createElement(OfferListLinks, { offerLists: [data.offerList], pathPrefix: "/".concat(role) })); }, [role]);
    var renderLotNumber = React.useCallback(function (data) {
        if (!data.offer)
            return '-';
        return "".concat(data.offer.lotNumber, ", ").concat(data.offer.processing, ", ").concat(data.offer.variety);
    }, []);
    var renderProcessings = React.useCallback(function (data) {
        return data.processings
            .map(function (processing) {
            return t("params.preOrderRequest.processings.".concat(processing));
        })
            .join(', ');
    }, []);
    var renderVarieties = React.useCallback(function (data) {
        return data.varieties
            .map(function (variety) { return t("params.preOrderRequest.varieties.".concat(variety)); })
            .join(', ');
    }, []);
    var statusOptions = Object.entries(t('params.preOrderRequest.statuses', {
        returnObjects: true,
    })).reduce(function (result, _a) {
        var key = _a[0], value = _a[1];
        result[decamelize(key)] = value;
        return result;
    }, {});
    var renderStatus = React.useCallback(function (data) { return (React.createElement(StatusTag, { className: data.status }, statusOptions[data.status])); }, [statusOptions]);
    var renderWeight = React.useCallback(function (data) { return weightFormat(data.weight); }, []);
    var renderRoasterName = React.useCallback(function (data) {
        var roaster = data.roaster;
        return React.createElement("span", null, t('fullname', roaster));
    }, [t]);
    var renderRoasterCommunityManager = React.useCallback(function (data) {
        var _a;
        var roaster = data.roaster;
        return React.createElement("span", null, ((_a = roaster.communityManager) === null || _a === void 0 ? void 0 : _a.name) || '');
    }, []);
    var renderCountryOffice = React.useCallback(function (data) {
        var name = data.roaster.country.office.name;
        return name;
    }, []);
    var renderEditStatus = React.useCallback(function (props) {
        var value = props.value, onChange = props.onChange;
        var handleChange = function (event) {
            onChange(event.target.value);
        };
        return (React.createElement(Select, { value: value, onChange: handleChange },
            React.createElement(MenuItem, { value: "active" }, t('params.preOrderRequest.statuses.active')),
            React.createElement(MenuItem, { value: "canceled" }, t('params.preOrderRequest.statuses.canceled'))));
    }, [t]);
    var renderEditProcessings = React.useCallback(function (_a) {
        var value = _a.value, onChange = _a.onChange;
        var handleChange = function (values, _checkedValue) {
            return onChange(values);
        };
        return (React.createElement(CheckboxGroup, { label: t('params.preOrderRequest.processing'), name: "processings", items: processingItems, value: value, onChange: handleChange }));
    }, []);
    var renderEditVarieties = React.useCallback(function (_a) {
        var value = _a.value, onChange = _a.onChange;
        var handleChange = function (values, _checkedValue) {
            return onChange(values);
        };
        return (React.createElement(CheckboxGroup, { label: t('params.preOrderRequest.variety'), name: "varieties", items: varietyItems, value: value, onChange: handleChange }));
    }, []);
    return {
        offerListName: {
            title: t('params.offerList.name'),
            render: renderOfferListLink,
            editable: 'never',
            sorting: false,
            filtering: false,
        },
        roasterId: {
            title: t('params.roasterId'),
            field: 'roaster.id',
            editable: 'never',
            sorting: false,
            filtering: false,
        },
        roasterName: {
            title: t('params.roaster.name'),
            render: renderRoasterName,
            editable: 'never',
            sorting: false,
            filtering: false,
        },
        roasterCommunityManager: {
            title: t('params.roaster.communityManager'),
            render: renderRoasterCommunityManager,
            field: 'roaster.communityManager',
            editable: 'never',
            sorting: false,
            filtering: false,
        },
        roasterCountryOfficeName: {
            title: t('params.office.name'),
            render: renderCountryOffice,
            field: 'roaster.country.office.name',
            editable: 'never',
            sorting: false,
            filtering: false,
        },
        roasterCompany: {
            title: t('params.roaster.company'),
            render: function (_a) {
                var roaster = _a.roaster;
                return roaster.company;
            },
            editable: 'never',
            sorting: false,
            filtering: false,
        },
        roasterStoreName: {
            title: t('params.roaster.storeName'),
            render: function (_a) {
                var roaster = _a.roaster;
                return roaster.storeName;
            },
            editable: 'never',
            sorting: false,
            filtering: false,
        },
        lotNumber: {
            title: t('params.offer.lotNumber'),
            render: renderLotNumber,
            editable: 'never',
            sorting: false,
            filtering: false,
        },
        processing: {
            field: 'processings',
            title: t('params.preOrderRequest.processing'),
            render: renderProcessings,
            editComponent: renderEditProcessings,
            sorting: false,
            filtering: false,
        },
        variety: {
            field: 'varieties',
            title: t('params.preOrderRequest.variety'),
            render: renderVarieties,
            editComponent: renderEditVarieties,
            sorting: false,
            filtering: false,
        },
        weight: numericColumn('weight', {
            render: renderWeight,
            sorting: false,
            filtering: false,
        }),
        status: {
            field: 'status',
            title: t('params.preOrderRequest.status'),
            render: renderStatus,
            editComponent: renderEditStatus,
            lookup: statusOptions,
            sorting: false,
            filtering: false,
        },
        notes: column('notes', {
            sorting: false,
            filtering: false,
            cellStyle: {
                minWidth: 200,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
            },
        }),
        mailSentAt: datetimeColumn('mailSentAt', {
            editable: 'never',
            sorting: false,
            filtering: false,
        }),
        requestedAt: datetimeColumn('requestedAt', {
            editable: 'never',
            sorting: false,
            filtering: false,
        }),
    };
}
export function useExclusiveLotColumn() {
    var t = useTranslation().t;
    var renderRoasterName = React.useCallback(function (exclusiveLot) {
        var roaster = exclusiveLot.roaster;
        if (roaster.company) {
            return "".concat(roaster.company, " ").concat(t('fullname', roaster), " (").concat(roaster.email, ")");
        }
        return "".concat(t('fullname', roaster), " (").concat(roaster.email, ")");
    }, [t]);
    return {
        roasterName: {
            field: 'roaster',
            title: t('params.roaster.name'),
            render: renderRoasterName,
            sorting: false,
            filtering: false,
        },
        quantity: numericColumn('quantity', {
            title: t('params.exclusiveLot.quantity'),
            initialEditValue: 0,
            render: function (_a) {
                var orderItem = _a.orderItem, quantity = _a.quantity;
                if ((orderItem === null || orderItem === void 0 ? void 0 : orderItem.order.status) === 'canceled')
                    return '-';
                return quantity;
            },
        }),
        typicaHandlingRate: numericColumn('typicaHandlingRate', {
            render: function (_a) {
                var typicaHandlingRate = _a.typicaHandlingRate;
                return formatPercentage(typicaHandlingRate);
            },
        }),
    };
}
